import React, { useMemo } from 'react';
import { Field, Form, FormikProps } from 'formik';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormHelperText from '@material-ui/core/FormHelperText';

import { FocusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.component';
import { FormikSelect } from '../../_core/Formik/formik-select.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { PlayerContactFields } from '../player-contact-fields.component';
import { IFormOption } from '../../_core/_ui/forms.component';
import { IParentPlayerContact } from '../../../lib/types';
import { contactsForPlayer } from './parent-add-contact.utils';
import { messages } from './parent-add-contact.messages';
import { BLOCKED_DUPLICATE_FIELDS } from './parent-add-contact.constants';
import { IParentAddContactFields } from './parent-add-contact.types';

const FORM_ID = 'parent-add-contact-form';

interface IParentAddContactProps {
  open: boolean;
  onClose: () => void;
  contacts: IParentPlayerContact[];
  playerOptions: IFormOption[];
  form: FormikProps<IParentAddContactFields>;
}

export const ParentAddContact: React.FC<IParentAddContactProps> = ({
  open,
  onClose,
  contacts,
  playerOptions,
  form,
}) => {
  const { values, status, isSubmitting } = form;

  // The selected player's association sport, null until one is picked — never
  // the default sport, which would offer fields that association may not collect.
  const selected = contacts.find((row) => row.playerId === values.player?.value);
  const sport = selected?.sport ?? null;

  // Everyone already on the selected player. The field block compares the
  // entered values against them; a blocked credential is a field error instead.
  const siblingContacts = useMemo(
    () => contactsForPlayer(contacts, values.player?.value as string),
    [contacts, values.player],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {/* Form inside the Dialog so its portal-rendered fields share the id focus targeting uses. */}
      <Form id={FORM_ID}>
        <FocusFirstInvalidField formId={FORM_ID} />
        <DialogTitle><IntlMsg {...messages.addContactTitle} /></DialogTitle>
        <DialogContent>
          <DialogContentText><IntlMsg {...messages.addContactIntro} /></DialogContentText>

          <Field
            fid="pac"
            name="player"
            component={FormikSelect}
            label={messages.lblPlayer}
            options={playerOptions}
          />

          <PlayerContactFields
            sport={sport}
            showSendToggles={false}
            showHeight={false}
            siblingContacts={siblingContacts}
            blockedFields={BLOCKED_DUPLICATE_FIELDS}
          />

          {status && (
            <Box mt={1}>
              <FormHelperText error>{status}</FormHelperText>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}><IntlMsg {...messages.cancel} /></Button>
          <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <CircularProgress size={18} style={{ marginRight: 8 }} />
                <IntlMsg {...messages.addContactSubmitting} />
              </>
            ) : <IntlMsg {...messages.addContactSubmit} />}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
